import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Calendar, Plus, Loader2, MapPin, Clock, Layers, Trash2, Copy, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, LayoutList, LayoutGrid, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "@/lib/toastBanner";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(d) {
  if (!d) return "";
  try {
    const [y, m, day] = d.split("-").map((s) => parseInt(s, 10));
    if (!y || !m || !day) return d;
    return new Date(y, m - 1, day).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return d; }
}

function formatTime(t) {
  if (!t) return "";
  try {
    const [h, m] = t.split(":");
    const hh = parseInt(h, 10);
    if (isNaN(hh)) return t;
    const period = hh >= 12 ? "PM" : "AM";
    const display = ((hh + 11) % 12) + 1;
    return `${display}:${m || "00"} ${period}`;
  } catch { return t; }
}

export default function EventList() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", date: "", start_time: "", location: "" });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [duplicatingId, setDuplicatingId] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    // Mobile users get a more navigable List view by default. They can toggle
    // back to Calendar via the view-switcher; the choice persists in localStorage.
    try {
      const stored = localStorage.getItem("events_view_mode");
      if (stored === "list" || stored === "calendar") return stored;
    } catch { /* ignore */ }
    if (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(max-width: 639px)").matches) {
      return "list";
    }
    return "calendar";
  }); // 'list' | 'calendar'
  const [sortKey, setSortKey] = useState("date"); // name | date | start_time | session_count | location
  const [sortDir, setSortDir] = useState("desc"); // 'asc' | 'desc'
  const [calCursor, setCalCursor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  // Group events by their YYYY-MM-DD date for the calendar grid.
  const eventsByDate = useMemo(() => {
    const map = {};
    for (const ev of events) {
      const key = (ev.date || "").trim();
      if (!key) continue;
      if (!map[key]) map[key] = [];
      map[key].push(ev);
    }
    // Earliest-time-first per day
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
    }
    return map;
  }, [events]);

  const calendarDays = useMemo(() => {
    const first = new Date(calCursor.getFullYear(), calCursor.getMonth(), 1);
    const startOffset = first.getDay(); // 0 = Sun
    const start = new Date(first);
    start.setDate(first.getDate() - startOffset);
    const days = [];
    for (let i = 0; i < 42; i += 1) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const ymd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      days.push({
        date: d,
        ymd,
        inMonth: d.getMonth() === calCursor.getMonth(),
      });
    }
    return days;
  }, [calCursor]);

  const monthLabel = calCursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const today = new Date();
  const todayYmd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  const openCreateForDate = (ymd) => {
    setForm({ name: "", date: ymd || "", start_time: "", location: "" });
    setOpen(true);
  };

  const toggleSort = (key) => {
    setSortKey((prevKey) => {
      if (prevKey === key) {
        // Same column → flip direction
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        return prevKey;
      }
      // New column → default direction (desc for date/time, asc for others)
      setSortDir(key === "date" || key === "start_time" ? "desc" : "asc");
      return key;
    });
  };

  const sortedEvents = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    const arr = [...events];
    arr.sort((a, b) => {
      let av; let bv;
      switch (sortKey) {
        case "name":
          av = (a.name || "").toLowerCase();
          bv = (b.name || "").toLowerCase();
          break;
        case "session_count":
          av = a.session_count || 0;
          bv = b.session_count || 0;
          break;
        case "attendee_count":
          av = a.attendee_count || 0;
          bv = b.attendee_count || 0;
          break;
        case "location":
          av = (a.location || "").toLowerCase();
          bv = (b.location || "").toLowerCase();
          break;
        case "start_time":
          // Combine date+time so events on different days sort intuitively.
          av = `${a.date || ""} ${a.start_time || ""}`;
          bv = `${b.date || ""} ${b.start_time || ""}`;
          break;
        case "date":
        default:
          av = `${a.date || ""} ${a.start_time || ""}`;
          bv = `${b.date || ""} ${b.start_time || ""}`;
          break;
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return arr;
  }, [events, sortKey, sortDir]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/events`, { withCredentials: true });
      setEvents(data?.events || []);
    } catch {
      toast.error("Failed to load events");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => setForm({ name: "", date: "", start_time: "", location: "" });

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error("Event name required"); return; }
    if (!form.date) { toast.error("Event date required"); return; }
    if (!form.start_time) { toast.error("Event start time required"); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/events`, {
        name: form.name.trim(),
        date: form.date,
        start_time: form.start_time,
        location: form.location.trim(),
      }, { withCredentials: true });
      toast.success("Event created");
      setOpen(false);
      resetForm();
      navigate(`/admin/events/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create event");
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    const ev = deleteTarget;
    if (!ev) return;
    try {
      await axios.delete(`${API}/events/${ev.id}`, { withCredentials: true });
      setEvents((prev) => prev.filter((e) => e.id !== ev.id));
      toast.success("Event deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete event");
    } finally { setDeleteTarget(null); }
  };

  const handleDuplicate = async (ev) => {
    setDuplicatingId(ev.id);
    try {
      const { data } = await axios.post(`${API}/events/${ev.id}/duplicate`, {}, { withCredentials: true });
      setEvents((prev) => [data, ...prev]);
      toast.success("Event duplicated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to duplicate event");
    } finally { setDuplicatingId(null); }
  };

  return (
    <div className="space-y-6" data-testid="events-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white">Events</h1>
          <p className="text-sm font-body text-[#6E7787] mt-1">Plan events and configure sessions and passports inside each one.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-white/10 bg-[#11141A] p-0.5" data-testid="events-view-toggle">
            <button
              type="button"
              onClick={() => { setViewMode("list"); try { localStorage.setItem("events_view_mode", "list"); } catch { /* ignore */ } }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-body transition-colors ${
                viewMode === "list" ? "bg-white/10 text-white" : "text-[#6E7787] hover:text-white"
              }`}
              data-testid="events-view-list"
            >
              <LayoutList className="h-3.5 w-3.5" /> List
            </button>
            <button
              type="button"
              onClick={() => { setViewMode("calendar"); try { localStorage.setItem("events_view_mode", "calendar"); } catch { /* ignore */ } }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-body transition-colors ${
                viewMode === "calendar" ? "bg-white/10 text-white" : "text-[#6E7787] hover:text-white"
              }`}
              data-testid="events-view-calendar"
            >
              <LayoutGrid className="h-3.5 w-3.5" /> Calendar
            </button>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate("/admin/events/attendees")}
            className="gap-2 border-white/15 text-white bg-transparent hover:bg-white/5"
            data-testid="events-attendee-catalog-btn"
          >
            <Users className="h-4 w-4" /> Attendee Catalog
          </Button>
          <Button
            onClick={() => { resetForm(); setOpen(true); }}
            className="gap-2"
            style={{ background: "var(--primary-color)", color: "var(--admin-button-fg, #fff)" }}
            data-testid="event-create-btn"
          >
            <Plus className="h-4 w-4" /> New Event
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#6E7787]" /></div>
      ) : viewMode === "calendar" ? (
        <div className="bg-white border border-slate-200 rounded-xl p-2 sm:p-4 shadow-sm" data-testid="events-calendar">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <h2 className="font-heading text-base font-semibold text-slate-900" data-testid="calendar-month-label">{monthLabel}</h2>
            <div className="inline-flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCalCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1))}
                className="h-8 w-8 p-0 bg-white border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                data-testid="calendar-prev"
                title="Previous month"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const t = new Date();
                  setCalCursor(new Date(t.getFullYear(), t.getMonth(), 1));
                }}
                className="h-8 px-3 text-xs bg-white border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                data-testid="calendar-today"
              >
                Today
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCalCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1))}
                className="h-8 w-8 p-0 bg-white border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                data-testid="calendar-next"
                title="Next month"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-px bg-slate-200 border border-slate-200 rounded-lg overflow-hidden">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <div key={d} className="bg-slate-50 px-1 sm:px-2 py-1 sm:py-1.5 text-[9px] sm:text-[10px] uppercase tracking-wider text-slate-500 font-body text-center font-semibold">
                <span className="sm:hidden">{d[0]}</span>
                <span className="hidden sm:inline">{d}</span>
              </div>
            ))}
            {calendarDays.map((cell) => {
              const cellEvents = eventsByDate[cell.ymd] || [];
              const isToday = cell.ymd === todayYmd;
              return (
                <div
                  key={cell.ymd}
                  className={`min-h-[64px] sm:min-h-[110px] p-1 sm:p-1.5 group transition-colors ${
                    cell.inMonth ? "bg-white" : "bg-slate-50"
                  } ${cellEvents.length === 0 ? "cursor-pointer hover:bg-slate-100" : ""}`}
                  onClick={() => { if (cellEvents.length === 0) openCreateForDate(cell.ymd); }}
                  data-testid={`calendar-cell-${cell.ymd}`}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`inline-flex items-center justify-center h-5 sm:h-6 min-w-[1.25rem] sm:min-w-[1.5rem] px-1 sm:px-1.5 rounded-full text-[11px] sm:text-[12px] font-body ${
                        isToday
                          ? "text-white font-bold"
                          : cell.inMonth
                          ? "text-slate-900 font-semibold"
                          : "text-slate-400 font-medium"
                      }`}
                      style={isToday ? { backgroundColor: "var(--primary-color, #4A90D9)" } : undefined}
                    >
                      {cell.date.getDate()}
                    </span>
                    {cellEvents.length === 0 && cell.inMonth ? (
                      <Plus className="h-3 w-3 text-slate-300 group-hover:text-slate-500 transition-colors" />
                    ) : null}
                  </div>
                  <div className="mt-0.5 sm:mt-1 space-y-0.5 sm:space-y-1">
                    {cellEvents.slice(0, 3).map((ev) => (
                      <button
                        key={ev.id}
                        type="button"
                        onClick={(e) => { e.stopPropagation(); navigate(`/admin/events/${ev.id}`); }}
                        className="w-full text-left inline-flex items-center gap-1 rounded px-1 sm:px-1.5 py-0.5 text-[9px] sm:text-[10px] font-body hover:opacity-90 transition-opacity"
                        style={{ background: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
                        title={`${ev.name} · ${formatTime(ev.start_time)}`}
                        data-testid={`calendar-event-${ev.id}`}
                      >
                        <Clock className="h-2.5 w-2.5 shrink-0 opacity-90 hidden sm:inline-block" />
                        <span className="truncate font-medium">{formatTime(ev.start_time)} · {ev.name}</span>
                      </button>
                    ))}
                    {cellEvents.length > 3 ? (
                      <p className="text-[9px] sm:text-[10px] font-body text-slate-500 px-1 sm:px-1.5 font-medium">+{cellEvents.length - 3} more</p>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-[11px] font-body text-slate-500 mt-3">
            Click an empty day to create an event. Click an event chip to open it.
          </p>
        </div>
      ) : events.length === 0 ? (
        <div className="bg-[#11141A] border border-dashed border-white/10 rounded-xl px-6 py-16 text-center" data-testid="events-empty">
          <Calendar className="h-10 w-10 mx-auto text-[#6E7787]" />
          <h3 className="mt-4 font-heading text-lg font-semibold text-white">No events yet</h3>
          <p className="text-sm font-body text-[#6E7787] mt-1">Create your first event to start adding sessions and passports.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="events-table">
          <div className="hidden sm:grid grid-cols-12 gap-4 px-4 py-2 text-xs text-[#6E7787] font-body uppercase tracking-wider">
            <SortHeader label="Name" sortKey="name" current={sortKey} dir={sortDir} onClick={toggleSort} className="col-span-4" />
            <SortHeader label="Date" sortKey="date" current={sortKey} dir={sortDir} onClick={toggleSort} className="col-span-2" />
            <SortHeader label="Start time" sortKey="start_time" current={sortKey} dir={sortDir} onClick={toggleSort} className="col-span-2" />
            <SortHeader label="Sessions" sortKey="session_count" current={sortKey} dir={sortDir} onClick={toggleSort} className="col-span-1" />
            <SortHeader label="Attendees" sortKey="attendee_count" current={sortKey} dir={sortDir} onClick={toggleSort} className="col-span-1" />
            <SortHeader label="Location" sortKey="location" current={sortKey} dir={sortDir} onClick={toggleSort} className="col-span-2" />
          </div>
          {sortedEvents.map((ev) => (
            <div
              key={ev.id}
              onClick={() => navigate(`/admin/events/${ev.id}`)}
              className="bg-[#11141A] border border-white/10 rounded-lg px-4 py-3.5 cursor-pointer hover:border-white/20 transition-all group"
              data-testid={`event-row-${ev.id}`}
            >
              <div className="sm:grid sm:grid-cols-12 sm:gap-4 sm:items-center">
                <div className="col-span-4 flex items-center gap-3">
                  <div
                    className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
                  >
                    <Calendar className="h-4 w-4" />
                  </div>
                  <span className="text-sm font-body font-medium text-white truncate">{ev.name}</span>
                </div>
                <div className="col-span-2 text-sm text-[#A1A9B8] font-body mt-1 sm:mt-0 inline-flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5 text-[#6E7787]" /> {formatDate(ev.date)}
                </div>
                <div className="col-span-2 text-sm text-[#A1A9B8] font-body mt-1 sm:mt-0 inline-flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 text-[#6E7787]" /> {formatTime(ev.start_time)}
                </div>
                <div className="col-span-1 text-sm text-[#A1A9B8] font-body mt-1 sm:mt-0 inline-flex items-center gap-1.5" data-testid={`event-session-count-${ev.id}`}>
                  <Layers className="h-3.5 w-3.5 text-[#6E7787]" /> {ev.session_count || 0}
                </div>
                <div className="col-span-1 text-sm text-[#A1A9B8] font-body mt-1 sm:mt-0 inline-flex items-center gap-1.5" data-testid={`event-attendee-count-${ev.id}`}>
                  <Users className="h-3.5 w-3.5 text-[#6E7787]" /> {ev.attendee_count || 0}
                </div>
                <div className="col-span-2 mt-1 sm:mt-0 flex items-center justify-between gap-2">
                  {ev.location ? (
                    <span className="text-sm text-[#A1A9B8] font-body inline-flex items-center gap-1.5 truncate" data-testid={`event-location-${ev.id}`}>
                      <MapPin className="h-3.5 w-3.5 text-[#6E7787]" /> <span className="truncate">{ev.location}</span>
                    </span>
                  ) : (
                    <span className="text-sm text-[#6E7787] font-body">—</span>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDuplicate(ev); }}
                    disabled={duplicatingId === ev.id}
                    className="p-1.5 text-[#6E7787] hover:text-white rounded-md hover:bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 disabled:opacity-40"
                    title="Duplicate event"
                    data-testid={`event-duplicate-${ev.id}`}
                  >
                    {duplicatingId === ev.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setDeleteTarget(ev); }}
                    className="p-1.5 text-[#6E7787] hover:text-red-400 rounded-md hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                    title="Delete event"
                    data-testid={`event-delete-${ev.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => { if (!saving) { setOpen(v); if (!v) resetForm(); } }}>
        <DialogContent className="bg-[#11141A] border-white/10 text-white" data-testid="event-create-modal">
          <DialogHeader>
            <DialogTitle className="font-body font-bold text-lg text-white">New Event</DialogTitle>
            <DialogDescription className="text-[#6E7787] font-body text-sm">Add a name, date, and start time. Location is optional.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-body text-[#A1A9B8]">Event name <span className="text-red-400">*</span></Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Spring Symposium"
                autoFocus
                data-testid="event-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-body text-[#A1A9B8]">Date <span className="text-red-400">*</span></Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="event-date-input" />
              </div>
              <div>
                <Label className="text-xs font-body text-[#A1A9B8]">Start time <span className="text-red-400">*</span></Label>
                <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="event-start-time-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-body text-[#A1A9B8] inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> Location <span className="text-[#6E7787]">(optional)</span></Label>
              <Input
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="Building / address / virtual link"
                data-testid="event-location-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); resetForm(); }} disabled={saving} data-testid="event-create-cancel">Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="event-create-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Event"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete this event?"
        description={deleteTarget ? `"${deleteTarget.name}" and all its sessions and passports will be permanently removed.` : ""}
      />
    </div>
  );
}

function SortHeader({ label, sortKey, current, dir, onClick, className = "" }) {
  const active = current === sortKey;
  return (
    <button
      type="button"
      onClick={() => onClick(sortKey)}
      className={`${className} inline-flex items-center gap-1 text-left hover:text-white transition-colors ${
        active ? "text-white" : ""
      }`}
      data-testid={`sort-header-${sortKey}`}
    >
      <span>{label}</span>
      <span className="flex flex-col leading-none" aria-hidden>
        <ChevronUp className={`h-2.5 w-2.5 -mb-0.5 ${active && dir === "asc" ? "text-white" : "text-[#3a4150]"}`} />
        <ChevronDown className={`h-2.5 w-2.5 ${active && dir === "desc" ? "text-white" : "text-[#3a4150]"}`} />
      </span>
    </button>
  );
}

